'use client';

import { css } from '@repo/pandacss/styled-system/css';
import { STATES_MAP } from '@repo/supergraph/marketplace/constants';
import { startTransition, useCallback, useMemo, useRef } from 'react';
import { MdClose } from 'react-icons/md';
import { useShallow } from 'zustand/shallow';
import type { OffersFilterStoreState } from './store/store';
import { sortByDefaultValue } from './store/store';
import { useOffersFilterStore } from './store/useOffersFilterStore';

type Props = {
  isMobile?: boolean;
  analyticsProps: { actionSection: string; pageIdentifier: string };
  onClearFilters?: () => void;
  regionCode?: string;
  onClearFiltersEvent: () => void;
  onRemoveFilterEvent: (attrs: { additional_attributes: { filter_cleared: string } }) => void;
};

const containerClass = css({ display: 'flex', marginTop: '2', justifyContent: 'space-between' });
const pillsWrapClass = css({
  display: 'flex',
  rowGap: '2',
  columnGap: {
    base: '2',
    md: '4',
  },
  flexWrap: 'wrap',
  letterSpacing: '[0.03rem]',
});

const pillClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  columnGap: '1',
  rowGap: '1',
  paddingLeft: '3',
  paddingRight: '3',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  borderTopLeftRadius: '[64px]',
  borderTopRightRadius: '[64px]',
  borderBottomRightRadius: '[64px]',
  borderBottomLeftRadius: '[64px]',
  fontSize: {
    base: 'xs',
    sm: 'sm',
  },
  fontWeight: 'medium',
  backgroundColor: 'neutral.lighter2',
  color: 'neutral.dark',
  cursor: 'pointer',
  _hover: { backgroundColor: '[rgba(0, 0, 0, 0.12)]' },
  letterSpacing: '[0.03rem]',
});

const locationPillClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  columnGap: '1',
  rowGap: '1',
  paddingInlineStart: '3',
  paddingInlineEnd: '3',
  paddingBlockStart: '1',
  paddingBlockEnd: '1',
  borderTopLeftRadius: '[64px]',
  borderTopRightRadius: '[64px]',
  borderBottomRightRadius: '[64px]',
  borderBottomLeftRadius: '[64px]',
  fontSize: {
    base: 'xs',
    sm: 'sm',
  },
  fontWeight: 'medium',
  backgroundColor: 'neutral.lighter2',
  cursor: 'default',
  color: 'neutral.dark',
});

const clearAllClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'neutral.dark',
  columnGap: '1',
  rowGap: '1',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '1',
  paddingBlockEnd: '1',
  borderTopLeftRadius: '[64px]',
  borderTopRightRadius: '[64px]',
  borderBottomRightRadius: '[64px]',
  borderBottomLeftRadius: '[64px]',
  fontSize: 'xs',
  fontWeight: 'medium',
  sm: { fontSize: 'sm' },
  backgroundColor: 'neutral.lighter2',
  cursor: 'pointer',
  _hover: { backgroundColor: '[rgba(0, 0, 0, 0.12)]' },
});

export function SelectedFilterPills({
  isMobile = false,
  onClearFilters,
  regionCode,
  onClearFiltersEvent,
  onRemoveFilterEvent,
}: Props) {
  const {
    appliedFilters,
    handleUpdateFilterFormState,
    handleApplyFilters,
    handleSortBy,
    sortBy,
    sortOptions,
    handleClearFilters,
  } = useOffersFilterStore(
    useShallow((state: OffersFilterStoreState) => ({
      appliedFilters: state.appliedFilters,
      handleUpdateFilterFormState: state.handleUpdateFilterFormState,
      handleApplyFilters: state.handleApplyFilters,
      handleSortBy: state.handleSortBy,
      sortBy: state.sortBy,
      sortOptions: state.sortOptions,
      handleClearFilters: state.handleClearFilters,
    })),
  );

  const updateTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const effectiveOnClearFilters = onClearFilters ?? handleClearFilters;

  const onClearAll = useCallback(() => {
    if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    updateTimeoutRef.current = setTimeout(() => {
      startTransition(() => {
        effectiveOnClearFilters();
        onClearFiltersEvent();
      });
    }, 100);
  }, [effectiveOnClearFilters, onClearFiltersEvent]);

  const handleRemoveFilter = useCallback(
    (selectedLabel: string) => {
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);

      const isProductType = appliedFilters?.selectedProductTypes.includes(selectedLabel);
      const isTermLength = appliedFilters?.selectedTermRanges.includes(selectedLabel);
      const isMinDeposit = appliedFilters?.selectedMinDepositRanges.includes(selectedLabel);
      const isMinBalance = appliedFilters?.selectedMinBalanceRanges.includes(selectedLabel);
      const isBonusOption = appliedFilters?.selectedBonusOptions.includes(selectedLabel);
      const isSortByLabel = sortOptions.find(o => o.label === selectedLabel);
      // Location pills are display-only on mobile — not removable

      updateTimeoutRef.current = setTimeout(() => {
        startTransition(() => {
          if (isProductType) {
            handleUpdateFilterFormState({
              selectedProductTypes:
                appliedFilters?.selectedProductTypes.filter(l => l !== selectedLabel) ?? [],
            });
          } else if (isTermLength) {
            handleUpdateFilterFormState({
              selectedTermRanges:
                appliedFilters?.selectedTermRanges.filter(l => l !== selectedLabel) ?? [],
            });
          } else if (isMinDeposit) {
            handleUpdateFilterFormState({
              selectedMinDepositRanges:
                appliedFilters?.selectedMinDepositRanges.filter(l => l !== selectedLabel) ?? [],
            });
          } else if (isMinBalance) {
            handleUpdateFilterFormState({
              selectedMinBalanceRanges:
                appliedFilters?.selectedMinBalanceRanges.filter(l => l !== selectedLabel) ?? [],
            });
          } else if (isBonusOption) {
            handleUpdateFilterFormState({
              selectedBonusOptions:
                appliedFilters?.selectedBonusOptions.filter(l => l !== selectedLabel) ?? [],
            });
          } else if (isSortByLabel) {
            handleSortBy(sortByDefaultValue);
          }
          handleApplyFilters();
          onRemoveFilterEvent({ additional_attributes: { filter_cleared: selectedLabel } });
        });
      }, 100);
    },
    [
      appliedFilters,
      handleUpdateFilterFormState,
      handleApplyFilters,
      handleSortBy,
      onRemoveFilterEvent,
      sortOptions,
    ],
  );

  const { allSelectedFilters } = useMemo(() => {
    const filters = [
      ...(appliedFilters?.selectedProductTypes ?? []),
      ...(appliedFilters?.selectedTermRanges ?? []),
      ...(appliedFilters?.selectedMinDepositRanges ?? []),
      ...(appliedFilters?.selectedMinBalanceRanges ?? []),
      ...(appliedFilters?.selectedBonusOptions ?? []),
    ];

    if (isMobile) {
      const sortByLabel = sortOptions.find(o => o.value === sortBy)?.label;
      if (sortByLabel) filters.push(sortByLabel);
      const location = appliedFilters?.selectedLocation ?? regionCode;
      const locationLabel = STATES_MAP[location as keyof typeof STATES_MAP];
      if (locationLabel) filters.push(locationLabel);
    }

    return { allSelectedFilters: filters };
  }, [
    appliedFilters?.selectedProductTypes,
    appliedFilters?.selectedLocation,
    appliedFilters?.selectedTermRanges,
    appliedFilters?.selectedMinDepositRanges,
    appliedFilters?.selectedMinBalanceRanges,
    appliedFilters?.selectedBonusOptions,
    sortBy,
    sortOptions,
    isMobile,
    regionCode,
  ]);

  const showClearAll = !isMobile && allSelectedFilters.length >= 2;

  return (
    <div className={containerClass}>
      <div className={pillsWrapClass}>
        {allSelectedFilters.map((filter, i) => {
          const location = appliedFilters?.selectedLocation ?? regionCode;
          const isLocation = location && STATES_MAP[location as keyof typeof STATES_MAP] === filter;

          if (isLocation) {
            return (
              <span key={`${filter}-${i}`} className={locationPillClass}>
                {filter}
              </span>
            );
          }

          return (
            <button
              key={`${filter}-${i}`}
              type="button"
              onClick={() => handleRemoveFilter(filter)}
              className={pillClass}
            >
              {filter}
              <MdClose size={18} aria-hidden />
            </button>
          );
        })}
        {showClearAll && (
          <button type="button" onClick={onClearAll} className={clearAllClass}>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
}
